import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import Cookies from 'js-cookie';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import 'styles/components/purchase/Timer/';
import { useTranslation } from 'react-i18next';
import { createTimerFormat } from '../../../utils/purchase/timer';

const propTypes = {
  variant: PropTypes.oneOf(['purchase', 'payment', 'exchange']),
  size: PropTypes.oneOf(['fixed', 'medium', 'default']),
  remainingTime: PropTypes.number,
};

const defaultProps = {
  variant: 'purchase',
  size: 'default',
};

const sizes = {
  fixed: 'timer-fixed',
  medium: 'timer-medium',
  default: '',
};

const HEADER_SELECTOR = '.header-fixed';
const LEGACY_HEADER_HEIGHT = 60;
const LEGACY_SMARTBANNER_OFFSET = 70;
// Mirrors `$sb-height` in `ui/atoms/SmartBanner.scss`: the banner is fixed at the top with this
// height while `smartbanner-hack` is on the header.
const SMARTBANNER_HEIGHT = 68;
const MOBILE_MAX_WIDTH = 1080;

/**
 * Whether the viewport is at or below the mobile breakpoint, read at call time.
 * @returns {boolean} True on mobile widths.
 */
const isMobileViewport = () => window.innerWidth <= MOBILE_MAX_WIDTH;

/**
 * Returns the page's fixed header, skipping the one `OverlayView` renders, so the choice does not
 * depend on DOM order. Filtered with `closest` rather than a complex `:not()` selector, which older
 * Android WebViews (Chrome < 88) reject with a SyntaxError.
 * @returns {HTMLElement|null} The page header, or null when the page has none.
 */
const getPageHeader = () =>
  Array.from(document.querySelectorAll(HEADER_SELECTOR)).find(
    (element) => !element.closest('.overlay-view'),
  ) || null;

/**
 * Returns the top offset that keeps the fixed timer right under the page header.
 *
 * On mobile the header is not fixed: it scrolls away with the page, so the timer follows the
 * header's bottom edge until it reaches the top of the viewport, then stays pinned at 0. The edge
 * is measured rather than assumed to be 60px, so the offset holds if a brand's header height
 * changes, and it also covers the smart banner, which pushes the header down via
 * `.smartbanner-hack`. While the smart banner is visible (it is fixed at the top of the viewport),
 * the timer pins under the banner instead of at 0, so it never slides beneath it.
 * @param {HTMLElement|null} timerElement - The timer container, used by the legacy fallback.
 * @returns {number} The top offset in pixels.
 */
export const getTimerTopPosition = (timerElement) => {
  const header = getPageHeader();
  if (header) {
    const minTop = header.classList.contains('smartbanner-hack') ? SMARTBANNER_HEIGHT : 0;
    return Math.max(minTop, Math.round(header.getBoundingClientRect().bottom));
  }

  // No fixed header on the page: keep the previous 60px-topbar approximation.
  const calculatedPosition = Math.max(0, LEGACY_HEADER_HEIGHT - window.scrollY);
  return timerElement?.className.includes('smartbanner-hack')
    ? calculatedPosition + LEGACY_SMARTBANNER_OFFSET
    : calculatedPosition;
};
/**
 * A timer component that displays the remaining time until a purchase is finalized.
 * @param {Object} props - The component props.
 * @param {string} props.size - The size of the timer ('fixed' or 'inline').
 * @param {string} props.variant - The visual style of the timer ('light' or 'dark').
 * @param {number} [props.remainingTime=0] - The remaining time in seconds.
 * @returns {JSX.Element|null} - The rendered timer component or null if the timer is not enabled.
 */
const Timer = ({ size, variant, remainingTime }) => {
  const { env } = useSelector((state) => state.whitelabelConfig);
  const { features } = useSelector((state) => state.whitelabelConfig);
  const { t } = useTranslation('purchase');
  const timerContainerRef = useRef(null);
  const [timeDisplay, setTimeDisplay] = useState('');

  const smartbannerCookie = Cookies.get('smartbanner_exited');
  let initialPosition = LEGACY_HEADER_HEIGHT;
  if (env.smartBanner?.enabled && !smartbannerCookie) {
    initialPosition = LEGACY_HEADER_HEIGHT + LEGACY_SMARTBANNER_OFFSET;
  }

  // `null` means desktop: no inline offset. The viewport is read on every update rather than
  // once per render so a resize across the breakpoint sets or clears the offset.
  const mobileInitialPosition = isMobileViewport() ? initialPosition : null;
  const [topPosition, setTopPosition] = useState(mobileInitialPosition);

  const updateTopPosition = useCallback(() => {
    setTopPosition(isMobileViewport() ? getTimerTopPosition(timerContainerRef.current) : null);
  }, []);

  useEffect(() => {
    if (env.smartBanner?.enabled && smartbannerCookie) {
      updateTopPosition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartbannerCookie]);

  // Layout effect so the first paint already has the measured offset, not the 60px default.
  // The header can also change height without a scroll (async menu items), hence the
  // ResizeObserver, or move without resizing: the smart banner adds `smartbanner-hack` to it after
  // mount, which shifts it down, hence the class MutationObserver.
  useLayoutEffect(() => {
    updateTopPosition();
    window.addEventListener('scroll', updateTopPosition, { passive: true });
    window.addEventListener('resize', updateTopPosition);

    const header = getPageHeader();
    const headerObserver =
      header && typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(updateTopPosition)
        : null;
    headerObserver?.observe(header);
    const headerClassObserver =
      header && typeof MutationObserver !== 'undefined'
        ? new MutationObserver(updateTopPosition)
        : null;
    headerClassObserver?.observe(header, { attributes: true, attributeFilter: ['class'] });

    return () => {
      window.removeEventListener('scroll', updateTopPosition);
      window.removeEventListener('resize', updateTopPosition);
      headerObserver?.disconnect();
      headerClassObserver?.disconnect();
    };
  }, [updateTopPosition]);

  // Applied imperatively with `important` priority: the hybrid apps (saas-app FunnelWebView)
  // inject `.timer-fixed { top: var(--timer-fixed) !important }` with a hardcoded 60px offset,
  // which beats a plain inline `top`. An inline `!important` declaration outranks any stylesheet
  // `!important`, so the funnel owns the timer position without requiring an app release.
  useLayoutEffect(() => {
    const timerElement = timerContainerRef.current;
    if (!timerElement) return;
    if (topPosition !== null) {
      timerElement.style.setProperty('top', `${topPosition}px`, 'important');
    } else {
      timerElement.style.removeProperty('top');
    }
  });

  const showBlock = size === 'fixed';

  useEffect(() => {
    const remainingTimeFormat = remainingTime ? createTimerFormat(remainingTime, 'mm:ss') : '';
    setTimeDisplay(remainingTimeFormat);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remainingTime]);

  if (!features.PURCHASE_TIMER_ENABLED) return null;

  return (
    <div>
      {showBlock && <div className="timer-block" />}
      <div ref={timerContainerRef} className={`timer ${sizes[size]} ${variant}-variant`}>
        <p className="timer-copy">
          {t('finalize_at', { context: variant })}:<span>{timeDisplay}</span>
        </p>
      </div>
    </div>
  );
};

Timer.propTypes = propTypes;
Timer.defaultProps = defaultProps;

export default Timer;
